/**
 * Home / lobby screen: shown when the player is not in a game.
 * Lists nearby waiting games (polled via findGames) and lets you create one.
 */
import A from "aberdeen";
import * as S from "staffa";
import { store } from "../store";
import type { LobbyGame } from "../api-types";
import { drawSectionHeader, drawScanSpinner } from "./theme";
import { plus, logIn, radio } from "staffa/icons.js";

/** Reactive list of nearby games — updated in place by the 5s poll. */
const $games: LobbyGame[] = A.proxy([]);
/** True only while the very first `findGames` request is in flight. */
const $loading = A.proxy(true);

const POLL_MS = 5_000;

export function drawLobby() {
	A("div display:flex flex-direction:column gap:$3", () => {
		drawNameField();
		drawNearbyGames();
		S.button({
			content: "CREATE A GAME",
			icon: () => plus({ size: "1.2em" }),
			attrs: "w:100% .large",
			click: () => void store.createGame(),
		});
	});
}

function drawNameField() {
	S.textline({
		label: "YOUR CALLSIGN",
		bind: A.ref(store.$, "name"),
		inputAttrs: "text-transform:uppercase letter-spacing:0.06em",
		change: (e: Event) => store.saveName((e.target as HTMLInputElement).value),
	});
}

function drawNearbyGames() {
	A("div display:flex flex-direction:column gap:$2", () => {
		drawSectionHeader("NEARBY GAMES", {
			icon: radio,
			trailing: () => drawScanSpinner(),
		});

		A(() => {
			const fix = store.$.fix;
			if (!fix) return drawEmpty("WAITING FOR GPS…");
			if ($loading.value && $games.length === 0) return drawEmpty("SCANNING…");
			if (!$games.length) return drawEmpty("None yet…");
			A("div display:flex flex-direction:column gap:$2", () => {
				A.onEach($games, ($g) => drawGameRow($g), ($g) => $g.distM);
			});
		});

		// Poll for nearby games every POLL_MS while the lobby is mounted. A.clean
		// tears the interval down when the surrounding scope is destroyed (e.g.
		// when the player joins/leaves a game).
		const tick = () => {
			if (!store.$.fix) return;
			store
				.findGames()
				?.promise.then((list) => A.copy($games, list))
				.catch(() => {})
				.finally(() => {
					$loading.value = false;
				});
		};
		tick();
		const id = setInterval(tick, POLL_MS);
		A.clean(() => clearInterval(id));
	});
}

/** Muted placeholder card for the GPS-pending / scanning / empty states. */
function drawEmpty(text: string) {
	A("div pv:$3 fg:$s-muted letter-spacing:0.05em text-align:center #", text);
}

function drawGameRow($g: LobbyGame) {
	S.box({
		attrs: ".primary",
		contentAttrs:
			"display:flex align-items:center justify-content:space-between gap:$3",
		content: () => {
			A("div display:flex flex-direction:column gap:$1 min-width:0", () => {
				A(`div font-weight:700 text-transform:uppercase #${$g.ownerName}'s game`);
				A("div display:flex align-items:center gap:$2 flex-wrap:wrap", () => {
					A(
						`div font-size:0.78rem fg:$s-muted letter-spacing:0.04em #${$g.playerCount} PLAYER(S) · ${$g.distM}M AWAY`,
					);
					A.onEach(
						$g.roles,
						($r) =>
							A("div w:10px h:10px r:2px title=", $r.name, "background:", $r.color),
						($r) => $r.id,
					);
				});
			});
			S.button({
				content: "JOIN",
				icon: () => logIn({ size: "1.1em" }),
				attrs: ".small",
				click: () => void store.joinGame($g.id),
			});
		},
	});
}
